import { KeyboardEvent, MouseEvent } from 'react';

// constants
import { ROLES, TAB_INDEX } from 'shared/constants';

/**
 * Hook to make non-button elements keyboard accessible
 * Handles Enter and Space key presses as clicks
 *
 * @param onClick - The click handler function
 * @returns Object with event handlers and ARIA attributes
 *
 * @example
 * const MyComponent = () => {
 *   const handleClick = () => console.log('clicked');
 *   const keyboardProps = useKeyboardClick(handleClick);
 *
 *   return <div {...keyboardProps}>Click me</div>;
 * };
 */
export const useKeyboardClick = <T extends HTMLElement = HTMLElement>(
  onClick: (event: any) => void,
  options: {
    disabled?: boolean;
    preventDefault?: boolean;
    role?: string;
  } = {}
) => {
  const { disabled = false, preventDefault = true } = options;

  const handleKeyDown = (event: KeyboardEvent<T>) => {
    // Only handle Enter and Space keys
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    if (disabled) {
      event.preventDefault();
      return;
    }

    if (preventDefault) {
      event.preventDefault();
    }

    onClick(event);
  };

  const handleClick = (event: MouseEvent<T>) => {
    if (disabled) {
      event.preventDefault();
      return;
    }

    onClick(event);
  };

  return {
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    role: options.role || ROLES.BUTTON,
    tabIndex: disabled ? TAB_INDEX.ACTION_VISIBILITY : TAB_INDEX.ACTION_INVISIBILITY,
    'aria-disabled': disabled ? true : undefined,
  };
};

export default useKeyboardClick;
